<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PrimaryButton, SecondaryButton, DatePicker } from '@es-bill/es-bill-common-components';
import {
  BILLING_VARIANTS,
  eurInputToMinor,
  minorToEurInput,
  perKwhInputToTariff,
} from '@/utils/serviceFeeViewUtils.js';

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  // fee entry to edit, null for a new one
  fee: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'save']);

const form = ref(makeEmptyForm());
const errorMessage = ref('');

function makeEmptyForm() {
  return {
    note: '',
    dateStart: '',
    dateEnd: '',
    billingVariant: BILLING_VARIANTS.CC_SEPERATE,
    priceFixedEur: '',
    pricePerKwhEur: '',
    pricePerCommunityMonthlyEur: '',
    pricePerMeteringPointMonthlyEur: '',
    scale: [],
    minimumAmountMonthlyEur: '',
  };
}

function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().substring(0, 10);
}

// the form keeps dates as YYYY-MM-DD strings, the DatePicker works on Date objects;
// noon keeps the calendar day stable between the local zone and the display zone
function dateFromForm(value) {
  return value ? new Date(value + 'T12:00:00') : null;
}

function dateToForm(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const dateStartPicker = computed({
  get: () => dateFromForm(form.value.dateStart),
  set: (v) => { form.value.dateStart = dateToForm(v); },
});

const dateEndPicker = computed({
  get: () => dateFromForm(form.value.dateEnd),
  set: (v) => { form.value.dateEnd = dateToForm(v); },
});

watch(() => props.modelValue, (open) => {
  if (!open) return;
  errorMessage.value = '';
  if (!props.fee) {
    form.value = makeEmptyForm();
    return;
  }
  const fee = props.fee;
  form.value = {
    note: fee.note || '',
    dateStart: toDateInput(fee.dateStart),
    dateEnd: toDateInput(fee.dateEnd),
    billingVariant: fee.billingVariant || BILLING_VARIANTS.CC_SEPERATE,
    priceFixedEur: minorToEurInput(fee.priceFixedMinor),
    pricePerKwhEur: fee.pricePerkWh !== undefined && fee.pricePerkWh !== null
      ? String(fee.pricePerkWh * Math.pow(10, -(fee.pricePerkWhFraction ?? 6)))
      : '',
    pricePerCommunityMonthlyEur: minorToEurInput(fee.pricePerCommunityMonthlyMinor),
    pricePerMeteringPointMonthlyEur: minorToEurInput(fee.pricePerMeteringPointMonthlyMinor),
    scale: (fee.meteringPointPriceScaleMonthlyMinor || []).map(s => ({
      startAmount: s.startAmount,
      endAmount: s.endAmount ?? '',
      pricePerUnitMonthlyEur: minorToEurInput(s.pricePerUnitMonthlyMinor),
    })),
    minimumAmountMonthlyEur: minorToEurInput(fee.minimumAmountMonthlyMinor),
  };
});

const billingVariantOptions = Object.values(BILLING_VARIANTS).map(v => ({
  value: v,
  title: v,
}));

function addScaleStep() {
  const last = form.value.scale[form.value.scale.length - 1];
  const nextStart = last && last.endAmount ? Number(last.endAmount) + 1 : (last ? '' : 1);
  form.value.scale.push({ startAmount: nextStart, endAmount: '', pricePerUnitMonthlyEur: '' });
}

function removeScaleStep(index) {
  form.value.scale.splice(index, 1);
}

function buildFee() {
  const f = form.value;
  const fee = {
    note: f.note,
    billingVariant: f.billingVariant,
  };
  if (f.dateStart) fee.dateStart = f.dateStart;
  if (f.dateEnd) fee.dateEnd = f.dateEnd;

  const fixed = eurInputToMinor(f.priceFixedEur);
  if (fixed !== undefined) fee.priceFixedMinor = fixed;

  const perKwh = perKwhInputToTariff(f.pricePerKwhEur);
  if (perKwh !== undefined) {
    fee.pricePerkWh = perKwh;
    fee.pricePerkWhFraction = 6;
  }

  const perCommunity = eurInputToMinor(f.pricePerCommunityMonthlyEur);
  if (perCommunity !== undefined) fee.pricePerCommunityMonthlyMinor = perCommunity;

  const perMp = eurInputToMinor(f.pricePerMeteringPointMonthlyEur);
  if (perMp !== undefined) fee.pricePerMeteringPointMonthlyMinor = perMp;

  const scale = f.scale
    .filter(s => s.startAmount !== '' && s.pricePerUnitMonthlyEur !== '')
    .map(s => {
      const step = {
        startAmount: Number(s.startAmount),
        pricePerUnitMonthlyMinor: eurInputToMinor(s.pricePerUnitMonthlyEur),
      };
      if (s.endAmount !== '' && s.endAmount !== undefined && s.endAmount !== null)
        step.endAmount = Number(s.endAmount);
      return step;
    });
  if (scale.length) fee.meteringPointPriceScaleMonthlyMinor = scale;

  const minimum = eurInputToMinor(f.minimumAmountMonthlyEur);
  if (minimum !== undefined) fee.minimumAmountMonthlyMinor = minimum;

  return fee;
}

function validateForm(fee) {
  if (!fee.dateStart)
    return t('serviceFees.validation.dateStartRequired');
  if (fee.dateEnd && fee.dateEnd < fee.dateStart)
    return t('serviceFees.validation.dateOrder');
  const hasPrice = ['priceFixedMinor', 'pricePerkWh', 'pricePerCommunityMonthlyMinor',
    'pricePerMeteringPointMonthlyMinor', 'minimumAmountMonthlyMinor']
    .some(k => fee[k] !== undefined) ||
    (fee.meteringPointPriceScaleMonthlyMinor && fee.meteringPointPriceScaleMonthlyMinor.length > 0);
  if (!hasPrice)
    return t('serviceFees.validation.priceRequired');
  const scale = fee.meteringPointPriceScaleMonthlyMinor || [];
  for (let i = 0; i < scale.length; i++) {
    if (i > 0 && scale[i].startAmount <= scale[i - 1].startAmount)
      return t('serviceFees.validation.scaleOrder');
    if (i < scale.length - 1 && scale[i].endAmount === undefined)
      return t('serviceFees.validation.scaleOpenEnd');
  }
  return '';
}

function onSave() {
  const fee = buildFee();
  const error = validateForm(fee);
  if (error) {
    errorMessage.value = error;
    return;
  }
  // keep untouched persistence fields of an edited entry (_id, __v of subdocs)
  if (props.fee && props.fee._id) fee._id = props.fee._id;
  emit('save', fee);
  emit('update:modelValue', false);
}

function onCancel() {
  emit('update:modelValue', false);
}
</script>

<template>
  <v-dialog :model-value="modelValue" persistent max-width="800" @update:model-value="v => emit('update:modelValue', v)">
    <v-card class="pa-6">
      <v-card-title>{{ props.fee ? t('serviceFees.editFee') : t('serviceFees.addFee') }}</v-card-title>
      <v-card-text>
        <v-text-field v-model="form.note" :label="t('serviceFees.note')" bg-color="white" variant="outlined" density="compact" />

        <v-row dense>
          <v-col cols="6">
            <DatePicker v-model="dateStartPicker" :placeholder="t('serviceFees.dateStart')" :has-day-of-week="false" />
          </v-col>
          <v-col cols="6">
            <DatePicker v-model="dateEndPicker" :placeholder="t('serviceFees.dateEnd')" :has-day-of-week="false" />
          </v-col>
        </v-row>

        <v-select
          v-model="form.billingVariant"
          :items="billingVariantOptions"
          :label="t('serviceFees.billingVariant')"
          bg-color="white" variant="outlined" density="compact"
        >
          <template v-slot:selection="{ item }">{{ t(`serviceFees.billingVariants.${item.value}`) }}</template>
          <template v-slot:item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps" :title="t(`serviceFees.billingVariants.${item.value}`)" />
          </template>
        </v-select>

        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.priceFixedEur" :label="t('serviceFees.priceFixed')" suffix="€" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.pricePerKwhEur" :label="t('serviceFees.pricePerKwh')" suffix="€/kWh" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.pricePerCommunityMonthlyEur" :label="t('serviceFees.pricePerCommunityMonthly')" suffix="€" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.pricePerMeteringPointMonthlyEur" :label="t('serviceFees.pricePerMeteringPointMonthly')" suffix="€" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>

        <p class="text-subtitle-2 mt-2 mb-1">{{ t('serviceFees.scaleTitle') }}</p>
        <v-row v-for="(step, index) in form.scale" :key="index" dense align="center">
          <v-col cols="3">
            <v-text-field v-model="step.startAmount" type="number" :label="t('serviceFees.scaleStart')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="3">
            <v-text-field v-model="step.endAmount" type="number" :label="t('serviceFees.scaleEnd')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="4">
            <v-text-field v-model="step.pricePerUnitMonthlyEur" :label="t('serviceFees.scalePrice')" suffix="€" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="2">
            <v-icon :title="t('delete')" @click="removeScaleStep(index)">mdi-delete</v-icon>
          </v-col>
        </v-row>
        <SecondaryButton size="small" :text="t('serviceFees.addScaleStep')" @click="addScaleStep" />

        <v-text-field
          v-model="form.minimumAmountMonthlyEur"
          class="mt-4"
          :label="t('serviceFees.minimumAmountMonthly')"
          suffix="€"
          bg-color="white" variant="outlined" density="compact"
        />

        <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mt-2">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <SecondaryButton :text="t('cancel')" @click="onCancel" />
        <PrimaryButton :text="t('save')" @click="onSave" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
